import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import { DocumentIcon } from '@heroicons/react/24/solid';
import SpinnerLoad from 'views/finance/moduleComponents/SpinnerLoad';
import { useFinanceProfile } from 'views/finance/moduleServices/finance_profilecontext';

const formatValue = (value) => {
  return value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value.toFixed(1);
};

const ChartBarCateg = ({ data, selectedFlowCategL0, selectedFlowType, setSelectedFlowType, loading = false }) => {
  const { defaultCurrency } = useFinanceProfile();
  const sortedData = [...data].sort((a, b) => b.sum - a.sum);
  
  const totalSum = sortedData.reduce((sum, item) => sum + item.sum, 0);

  const barColor = selectedFlowType === 'Out' ? '#C00000' : '#166534';

  const hasData = data.length > 0 && totalSum > 0;

  if (loading) {
    return (
      <div className="h-76 min-w-[300px] min-h-[250px] bg-white shadow-lg rounded-xl p-3 flex flex-col relative overflow-hidden">
        <div className="mb-2">
          <div className="h-6 bg-gray-400 rounded animate-pulse w-64 mb-2"></div>
          <div className="mt-2">
            <div className="bg-gray-100 rounded-full p-0.5 inline-flex">
              <div className="px-3 py-1 text-xs font-medium bg-gray-400 rounded-l-full animate-pulse w-8"></div>
              <div className="px-3 py-1 text-xs font-medium bg-gray-400 rounded-r-full animate-pulse w-8"></div>
            </div>
          </div>
        </div>
        <div className="flex-1 relative">
          <div className="flex items-end justify-around h-full">
            <div className="flex flex-col items-center">
              <div className="w-12 bg-gray-400 rounded-t animate-pulse mb-2" style={{ height: '120px' }}></div>
              <div className="h-3 bg-gray-400 rounded animate-pulse w-20"></div>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-12 bg-gray-400 rounded-t animate-pulse mb-2" style={{ height: '80px' }}></div>
              <div className="h-3 bg-gray-400 rounded animate-pulse w-20"></div>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-12 bg-gray-400 rounded-t animate-pulse mb-2" style={{ height: '160px' }}></div>
              <div className="h-3 bg-gray-400 rounded animate-pulse w-20"></div>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-12 bg-gray-400 rounded-t animate-pulse mb-2" style={{ height: '60px' }}></div>
              <div className="h-3 bg-gray-400 rounded animate-pulse w-20"></div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-2 right-2 bg-white bg-opacity-90 backdrop-blur-sm border border-gray-200 rounded-lg px-3 py-2 shadow-md">
          <div className="h-3 bg-gray-400 rounded animate-pulse w-12 mb-1"></div>
          <div className="h-4 bg-gray-400 rounded animate-pulse w-20"></div>
        </div>
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70">
          <SpinnerLoad />
        </div>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const subcategories = data.subcategories || {};
      return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 max-w-xs">
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {Object.entries(subcategories)
              .sort(([, a], [, b]) => b - a)
              .map(([subcat, value]) => (
                <div key={subcat} className="flex justify-between text-xs">
                  <span className="text-gray-700 truncate mr-2">{subcat}</span>
                  <span className="font-medium text-gray-800">
                    {defaultCurrency} {Number(value).toFixed(2)}
                  </span>
                </div>
              ))}
            <div className="flex justify-between text-xs border-t pt-1 mt-1">
              <span className="text-gray-700 font-semibold">Total</span>
              <span className="font-bold text-gray-800">
                {defaultCurrency} {Number(data.sum).toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="h-76 min-w-[300px] min-h-[250px] bg-white shadow-lg rounded-xl p-3 flex flex-col relative">
      <div className="mb-2 flex justify-between items-center flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="text-md font-semibold">by Category</span>
          <span className="text-xs text-gray-500 bg-gray-100 rounded px-2 py-0.5 font-medium">
            {selectedFlowCategL0}
          </span>
        </div>
        <div>
          <div className="bg-gray-100 rounded-full p-0.5 inline-flex">
            {['In', 'Out'].map((type, index) => (
              <button
                key={type}
                onClick={() => setSelectedFlowType(type)}
                className={`
                  px-3 py-1 text-xs font-medium transition-all
                  ${index === 0 ? 'rounded-l-full' : 'rounded-r-full'}
                  ${selectedFlowType === type
                    ? type === 'In'
                      ? 'text-white bg-green-400'
                      : 'text-white bg-red-400'
                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                  }
                `}
              >
                {type}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="flex-1 relative">
        {hasData ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={sortedData}
              layout="vertical"
              margin={{ top: 10, right: 30, bottom: 10, left: 30 }}
            >
              <XAxis type="number" hide />
              <YAxis
                type="category"
                dataKey="category"
                tick={{ fontSize: 12 }}
                width={80}
                interval={0}
                tickFormatter={(value) => value.replace(/ /g, '\u00A0')}
              />
              <Tooltip content={<CustomTooltip />} wrapperStyle={{ zIndex: 5 }} />
              <Bar dataKey="sum" fill={barColor}>
                <LabelList
                  dataKey="sum"
                  position="right"
                  fill="#000"
                  fontSize={12}
                  formatter={formatValue}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <div className="text-gray-400 mb-3">
                <DocumentIcon className="w-16 h-16 mx-auto" />
              </div>
              <div className="text-gray-500 text-sm font-medium mb-2">
                No flow data to show
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="absolute bottom-2 right-2 bg-white bg-opacity-90 backdrop-blur-sm border border-gray-200 rounded-lg px-3 py-2 shadow-md">
        <div className="text-xs text-gray-600 font-medium">Total</div>
        <div className="text-sm font-bold text-gray-800">
          {defaultCurrency} {totalSum.toFixed(2)}
        </div>
      </div>
    </div>
  );
};

export default ChartBarCateg;
